import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@clerk/tanstack-react-start";

import { Dialog, DialogContent } from "~/components/ui/dialog";
import { useInboundUtmParameters } from "~/hooks/use-inbound-utm-parameters";

import { SummitRegistration } from "./summit-registration";
import {
  checkUserSummitRegistrationOptions,
  getNextAvailableSummitOptions,
} from "~/lib/functions/summit-registration";

interface SummitRegistrationPopupProps {
  redirectUrl?: string;
  className?: string;
}

type RegisterLinkPredicate = (
  anchor: HTMLAnchorElement,
  currentUrl: URL,
) => boolean;

const REGISTER_HASH = "register";

const isRegisterHashLink: RegisterLinkPredicate = (anchor, currentUrl) => {
  const href = anchor.getAttribute("href");
  if (!href) {
    return false;
  }

  if (href.startsWith("#")) {
    return href === "#register";
  }

  try {
    const resolved = new URL(href, currentUrl);
    return (
      resolved.origin === currentUrl.origin &&
      resolved.pathname === currentUrl.pathname &&
      resolved.hash === "#register"
    );
  } catch {
    return false;
  }
};

function getAnchorFromEvent(event: MouseEvent): HTMLAnchorElement | null {
  const target = event.target;
  if (!(target instanceof Element)) {
    return null;
  }

  const element = target.closest("a");
  return element instanceof HTMLAnchorElement ? element : null;
}

export function SummitRegistrationPopup({
  redirectUrl,
  className,
}: SummitRegistrationPopupProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { isLoaded, isSignedIn, userId } = useAuth();
  const locationHash = useRouterState({
    select: (state) => state.location.hash,
  });
  const [isClient, setIsClient] = useState(false);
  const inboundUtmParameters = useInboundUtmParameters();

  const isOpen = locationHash === REGISTER_HASH;

  const updateHash = useCallback(
    (nextHash: string) => {
      void router.navigate({
        hash: nextHash,
        replace: true,
        resetScroll: false,
      });
    },
    [router],
  );

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    void queryClient
      .prefetchQuery(getNextAvailableSummitOptions)
      .catch((error) => {
        console.error("Failed to prefetch summit information:", error);
      });
  }, [queryClient]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !userId) {
      return;
    }

    void queryClient
      .prefetchQuery({
        ...checkUserSummitRegistrationOptions,
        queryKey: ["summit", "user-registration", userId],
      })
      .catch((error) => {
        console.error("Failed to prefetch summit registration state:", error);
      });
  }, [isLoaded, isSignedIn, queryClient, userId]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      const anchor = getAnchorFromEvent(event);
      if (!anchor) {
        return;
      }

      if (!isRegisterHashLink(anchor, new URL(window.location.href))) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      updateHash(REGISTER_HASH);
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [updateHash]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const normalizeHash = (hash: string) =>
      hash.startsWith("#") ? hash.slice(1) : hash;

    const handleHashChange = () => {
      const nextHash = normalizeHash(window.location.hash);
      if (nextHash === router.state.location.hash) {
        return;
      }

      updateHash(nextHash);
    };

    const existingHash = normalizeHash(window.location.hash);
    if (
      existingHash === REGISTER_HASH &&
      router.state.location.hash !== REGISTER_HASH
    ) {
      updateHash(REGISTER_HASH);
    }

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [router, updateHash]);

  if (!isClient) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (next) {
          if (!isOpen) {
            updateHash(REGISTER_HASH);
          }
          return;
        }

        if (router.state.location.hash !== "") {
          updateHash("");
        }
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto p-4 sm:p-8">
        <SummitRegistration
          redirectUrl={redirectUrl}
          className={className}
          inboundUtmParameters={inboundUtmParameters}
        />
      </DialogContent>
    </Dialog>
  );
}
